import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiCheckSquare, FiPlus, FiClock, FiAlertCircle, FiCheckCircle,
  FiX, FiTrash2, FiCalendar, FiTrendingDown, FiTarget
} from 'react-icons/fi';
import {
  listQCChecklists, getQCChecklistStats, createQCChecklist,
  deleteQCChecklist, getQCTemplates, getJobs
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TYPE_LABELS = {
  boundary: 'Boundary',
  topo: 'Topo',
  construction_staking: 'Construction Staking',
  alta: 'ALTA/NSPS',
  subdivision: 'Subdivision',
};

const STATUS_CONFIG = {
  in_progress: {
    label: 'In Progress',
    bg: '#fef3c7',
    color: '#92400e',
    border: '#fcd34d',
    icon: FiClock,
  },
  revisions_required: {
    label: 'Revisions Required',
    bg: '#fee2e2',
    color: '#991b1b',
    border: '#fca5a5',
    icon: FiAlertCircle,
  },
  approved: {
    label: 'Approved',
    bg: '#d1fae5',
    color: '#065f46',
    border: '#6ee7b7',
    icon: FiCheckCircle,
  },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.in_progress;
  const Icon = config.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
      backgroundColor: config.bg, color: config.color, border: `1px solid ${config.border}`,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={12} /> {config.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{
      flex: '1 1 180px', backgroundColor: 'white', borderRadius: '12px',
      padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0,
    }}>
      <div style={{
        width: '44px', height: '44px', borderRadius: '10px',
        backgroundColor: `${color}20`, color: color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={22} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          {label}
        </div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827', lineHeight: '1.1', marginTop: '2px' }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function QCDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManagePayroll = user?.can_manage_payroll || false;

  const [checklists, setChecklists] = useState([]);
  const [stats, setStats] = useState({ total_this_month: 0, in_progress: 0, revisions_required: 0, approved: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);

  // New checklist form state
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jobSearch, setJobSearch] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [newType, setNewType] = useState('boundary');
  const [draftedBy, setDraftedBy] = useState('');
  const [reviewedBy, setReviewedBy] = useState('');
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [listRes, statsRes] = await Promise.all([
        listQCChecklists(params),
        getQCChecklistStats(),
      ]);
      setChecklists(listRes.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error loading QC dashboard:', error);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load jobs and templates lazily when modal opens
  const openNewModal = async () => {
    setShowNewModal(true);
    setSelectedJob(null);
    setJobSearch('');
    setDraftedBy(user?.name || '');
    setReviewedBy('');
    setNewType('boundary');
    if (templates.length === 0) {
      try {
        const tplRes = await getQCTemplates();
        setTemplates(tplRes.data || []);
      } catch (e) { /* ignore */ }
    }
    if (jobs.length === 0) {
      try {
        const jobsRes = await getJobs('limit=200&sort_by=created_at&sort_order=desc');
        setJobs(jobsRes.data?.jobs || []);
      } catch (e) { /* ignore */ }
    }
  };

  const handleCreate = async () => {
    if (!selectedJob || !newType) {
      alert('Please select a job and a checklist type');
      return;
    }
    setCreating(true);
    try {
      const res = await createQCChecklist({
        job_id: selectedJob.id,
        checklist_type: newType,
        drafted_by: draftedBy,
        reviewed_by: reviewedBy,
      });
      setShowNewModal(false);
      navigate(`/qc/${res.data.id}`);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create checklist');
    }
    setCreating(false);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm('Delete this checklist? This cannot be undone.')) return;
    try {
      await deleteQCChecklist(id);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete');
    }
  };

  const filteredJobs = jobSearch.trim().length >= 2
    ? jobs.filter(j =>
        (j.job_number || '').toLowerCase().includes(jobSearch.toLowerCase()) ||
        (j.client_name || '').toLowerCase().includes(jobSearch.toLowerCase()) ||
        (j.property_address || '').toLowerCase().includes(jobSearch.toLowerCase())
      ).slice(0, 25)
    : [];

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px', flexWrap: 'wrap', gap: '12px',
      }}>
        <h1 style={{ margin: 0, fontSize: '26px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FiCheckSquare /> QA / QC Checklists
        </h1>
        <button
          onClick={openNewModal}
          style={{
            padding: '10px 20px', backgroundColor: '#2563eb', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
            fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
          }}
        >
          <FiPlus /> New QC Checklist
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <StatCard icon={FiCalendar} label="Created This Month" value={stats.total_this_month || 0} color="#2563eb" />
        <StatCard icon={FiCheckCircle} label="Approved This Month" value={stats.completed_this_month || 0} color="#16a34a" />
        <StatCard icon={FiTrendingDown} label="Avg Items Failed" value={stats.avg_items_failed_per_completed || 0} color="#dc2626" />
        <StatCard icon={FiTarget} label="Total Checklists" value={stats.total || 0} color="#7c3aed" />
      </div>

      {/* Analytics row: status breakdown + top failed items */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', marginBottom: '16px',
      }}>
        {/* Status breakdown bar chart */}
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '16px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 14px', fontSize: '14px', color: '#374151', fontWeight: '600' }}>
            Checklists by Status
          </h3>
          {(stats.total || 0) === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: '13px', padding: '10px 0' }}>No checklists yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { key: 'in_progress', label: 'In Progress', color: '#d97706', bg: '#fef3c7' },
                { key: 'revisions_required', label: 'Revisions Required', color: '#dc2626', bg: '#fee2e2' },
                { key: 'approved', label: 'Approved', color: '#16a34a', bg: '#d1fae5' },
              ].map(s => {
                const count = stats[s.key] || 0;
                const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                return (
                  <div key={s.key}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', fontSize: '12px',
                      color: '#374151', fontWeight: '500', marginBottom: '4px',
                    }}>
                      <span>{s.label}</span>
                      <span>{count} ({pct}%)</span>
                    </div>
                    <div style={{
                      height: '10px', backgroundColor: '#f3f4f6', borderRadius: '5px', overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%', width: `${pct}%`, backgroundColor: s.color,
                        borderRadius: '5px', transition: 'width 0.4s ease',
                        minWidth: count > 0 ? '4px' : '0',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top failed items */}
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '16px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 14px', fontSize: '14px', color: '#374151', fontWeight: '600' }}>
            Most Commonly Failed Items
          </h3>
          {(!stats.top_failed_items || stats.top_failed_items.length === 0) ? (
            <div style={{ color: '#9ca3af', fontSize: '13px', padding: '10px 0' }}>
              No failed items yet — great work!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {stats.top_failed_items.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '10px', alignItems: 'flex-start',
                  padding: '8px 10px', backgroundColor: '#fef2f2', borderRadius: '8px',
                  border: '1px solid #fecaca',
                }}>
                  <span style={{
                    flexShrink: 0, width: '24px', height: '24px', borderRadius: '50%',
                    backgroundColor: '#dc2626', color: 'white', fontSize: '12px', fontWeight: '700',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {item.fail_count}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: '#111827', lineHeight: '1.4' }}>
                      {item.item_text}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                      {item.category}
                    </div>
                  </div>
                </div>
              ))}
              {stats.completed_count > 0 && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                  Based on {stats.completed_count} reviewed checklist{stats.completed_count !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filter buttons */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap',
        backgroundColor: 'white', padding: '12px', borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        {[
          { key: 'all', label: 'All', count: stats.total },
          { key: 'in_progress', label: 'In Progress', count: stats.in_progress },
          { key: 'revisions_required', label: 'Revisions Required', count: stats.revisions_required },
          { key: 'approved', label: 'Approved', count: stats.approved },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            style={{
              padding: '8px 14px', borderRadius: '8px', border: '1px solid',
              borderColor: statusFilter === f.key ? '#2563eb' : '#e5e7eb',
              backgroundColor: statusFilter === f.key ? '#2563eb' : 'white',
              color: statusFilter === f.key ? 'white' : '#374151',
              cursor: 'pointer', fontSize: '13px', fontWeight: '600',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            {f.label}
            <span style={{
              padding: '1px 7px', borderRadius: '10px', fontSize: '11px',
              backgroundColor: statusFilter === f.key ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
              color: statusFilter === f.key ? 'white' : '#6b7280',
            }}>
              {f.count || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Table / List */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : checklists.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            <FiCheckSquare size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>No checklists found.</p>
            {statusFilter !== 'all' && (
              <p style={{ margin: '8px 0 0', fontSize: '13px' }}>Try changing the filter above.</p>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Job</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Type</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Reviewer</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {checklists.map(cl => {
                  const config = STATUS_CONFIG[cl.status] || STATUS_CONFIG.in_progress;
                  return (
                    <tr
                      key={cl.id}
                      onClick={() => navigate(`/qc/${cl.id}`)}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        backgroundColor: cl.status === 'in_progress' ? '#fffbeb'
                          : cl.status === 'revisions_required' ? '#fef2f2'
                          : cl.status === 'approved' ? '#f0fdf4'
                          : 'white',
                      }}
                    >
                      <td style={{ padding: '14px' }}>
                        <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                          {cl.job_number || `Job #${cl.job_id}`}
                        </div>
                        {cl.totals && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {cl.totals.pass || 0}/{cl.totals.total || 0} passed
                            {cl.totals.fail > 0 && (
                              <span style={{ color: '#dc2626', marginLeft: '6px' }}>· {cl.totals.fail} failed</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px', color: '#374151', fontSize: '13px' }}>
                        {TYPE_LABELS[cl.checklist_type] || cl.checklist_type}
                      </td>
                      <td style={{ padding: '14px' }}>
                        <StatusBadge status={cl.status} />
                      </td>
                      <td style={{ padding: '14px', color: '#374151', fontSize: '13px' }}>
                        {cl.reviewed_by || <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ padding: '14px', color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap' }}>
                        {formatDate(cl.created_at)}
                      </td>
                      <td style={{ padding: '14px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                          <Link
                            to={`/qc/${cl.id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              padding: '6px 12px', backgroundColor: '#eff6ff', color: '#2563eb',
                              border: '1px solid #bfdbfe', borderRadius: '6px',
                              textDecoration: 'none', fontSize: '12px', fontWeight: '600',
                            }}
                          >
                            Open
                          </Link>
                          {canManagePayroll && (
                            <button
                              onClick={(e) => handleDelete(cl.id, e)}
                              style={{
                                padding: '6px 8px', backgroundColor: '#fef2f2', color: '#dc2626',
                                border: '1px solid #fecaca', borderRadius: '6px',
                                cursor: 'pointer', display: 'flex', alignItems: 'center',
                              }}
                              title="Delete"
                            >
                              <FiTrash2 size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Checklist Modal */}
      {showNewModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '500px', width: '100%', maxHeight: 'calc(90dvh - 90px)', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>New QC Checklist</h3>
              <button onClick={() => setShowNewModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <FiX size={20} />
              </button>
            </div>

            {/* Job picker */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Job *
              </label>
              {selectedJob ? (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
                }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{selectedJob.job_number}</div>
                    {selectedJob.client_name && <div style={{ fontSize: '12px', color: '#6b7280' }}>{selectedJob.client_name}</div>}
                  </div>
                  <button
                    onClick={() => { setSelectedJob(null); setJobSearch(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '14px' }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search jobs by number, client, or address..."
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                      borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                    }}
                  />
                  {filteredJobs.length > 0 && (
                    <div style={{
                      marginTop: '4px', maxHeight: '200px', overflowY: 'auto',
                      border: '1px solid #e5e7eb', borderRadius: '8px',
                    }}>
                      {filteredJobs.map(j => (
                        <div
                          key={j.id}
                          onClick={() => { setSelectedJob(j); setJobSearch(''); }}
                          style={{
                            padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                            fontSize: '13px',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <strong>{j.job_number}</strong>
                          {j.client_name && <span style={{ color: '#6b7280' }}> — {j.client_name}</span>}
                          {j.property_address && <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '2px' }}>{j.property_address}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Type */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Checklist Type *
              </label>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '8px', fontSize: '14px', backgroundColor: 'white', boxSizing: 'border-box',
                }}
              >
                {(templates.length > 0 ? templates : Object.entries(TYPE_LABELS).map(([k, v]) => ({ key: k, label: v }))).map(t => (
                  <option key={t.key} value={t.key}>
                    {t.label}{t.item_count ? ` (${t.item_count} items)` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Drafted By */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Drafted By
              </label>
              <input
                type="text"
                value={draftedBy}
                onChange={(e) => setDraftedBy(e.target.value)}
                placeholder="Person who drafted the work"
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Reviewed By */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Reviewed By
              </label>
              <input
                type="text"
                value={reviewedBy}
                onChange={(e) => setReviewedBy(e.target.value)}
                placeholder="Person performing the QC review"
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNewModal(false)}
                style={{
                  padding: '10px 18px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                  borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !selectedJob}
                style={{
                  padding: '10px 18px', backgroundColor: (creating || !selectedJob) ? '#9ca3af' : '#2563eb',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: (creating || !selectedJob) ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '600',
                }}
              >
                {creating ? 'Creating…' : 'Create Checklist'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QCDashboard;
